<template>
  <div class="card h-100 wallet-summary-card">
    <div class="instrument-card-header">
      <h5 class="instrument-card-title">{{ group.label }}</h5>
    </div>
    <div class="card-body summary-card-body">
      <table class="table positions-summary-table">
        <tbody>
          <tr>
            <td :class="smallTextClass">{{ t("balanceLabel") }}</td>
            <td class="equity_align_right_heavy" :class="smallTextClass">
              {{ formatPrice(group.value, currency, 2).replace(" ", "&nbsp;") }}
            </td>
          </tr>
          <tr>
            <td :class="smallTextClass">{{ t("instrumentsLabel") }}</td>
            <td class="equity_align_right_heavy" :class="smallTextClass">
              {{ parseNumber(group.positionsCount, 0) }}
            </td>
          </tr>
          <tr>
            <td :class="smallTextClass">{{ t("returnLabel") }}</td>
            <td
              class="equity_align_right_heavy"
              :class="[smallTextClass, returnClass]"
            >
              <span class="return-value-with-icon">
                <span class="return-value-text">{{ formattedReturn }}</span>
                <img
                  v-if="returnValue !== 0"
                  class="quote-change-icon"
                  :src="returnValue > 0 ? quoteUpIcon : quoteDownIcon"
                  alt=""
                >
              </span>
            </td>
          </tr>
          <tr>
            <td :class="smallTextClass">{{ t("positionsPercentageLabel") }}</td>
            <td class="equity_align_right_heavy" :class="smallTextClass">
              <span class="progress-meter-value">
                <meter :value="positionsBalancePercent" min="0" max="100"></meter>
                <span class="progress-percent">{{ parseNumber(positionsBalancePercent) }}%</span>
              </span>
            </td>
          </tr>
          <tr>
            <td :class="smallTextClass">{{ t("balancePercentageLabel") }}</td>
            <td class="equity_align_right_heavy" :class="smallTextClass">
              <span class="progress-meter-value">
                <meter :value="totalBalancePercent" min="0" max="100"></meter>
                <span class="progress-percent">{{ parseNumber(totalBalancePercent) }}%</span>
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <button
      type="button"
      class="btn btn-primary w-90 m-2"
      :class="smallTextClass"
      @click.prevent="openBreakdown(group.key)"
    >
      {{ t("viewInstrumentsLabel") }}
    </button>
  </div>
</template>

<script>
import quoteDownIcon from "@/assets/images/Quote-Down.svg";
import quoteUpIcon from "@/assets/images/Quote-Up.svg";

export default {
  name: "WalletInstrumentTypeSummaryCard",
  props: {
    group: { type: Object, required: true },
    currency: { type: String, required: true },
    windowsWidth: { type: Number, required: true },
    totalBalance: { type: [Number, String], required: true },
    totalEquitiesValue: { type: [Number, String], required: true },
    translator: { type: Function, required: true },
    priceFormatter: { type: Function, required: true },
    numberParser: { type: Function, required: true },
    openBreakdown: { type: Function, required: true }
  },
  computed: {
    quoteDownIcon() {
      return quoteDownIcon;
    },
    quoteUpIcon() {
      return quoteUpIcon;
    },
    positionsBalancePercent() {
      return this.clampPercent(0 === parseFloat(this.totalEquitiesValue)
        ? 0
        : parseFloat(this.group.value) / parseFloat(this.totalEquitiesValue) * 100);
    },
    totalBalancePercent() {
      return this.clampPercent(0 === parseFloat(this.totalBalance)
        ? 0
        : parseFloat(this.group.value) / parseFloat(this.totalBalance) * 100);
    },
    returnValue() {
      return parseFloat(this.group.value) - parseFloat(this.group.avgValue);
    },
    returnPercent() {
      return 0 === parseFloat(this.group.avgValue)
        ? 0
        : this.returnValue / parseFloat(this.group.avgValue) * 100;
    },
    returnClass() {
      return {
        greenColor: 0 < this.returnValue,
        redColor: 0 > this.returnValue
      };
    },
    formattedReturn() {
      if (0 === parseFloat(this.group.avgValue)) {
        return `${this.parseNumber(0)} (${this.parseNumber(0)}%)`;
      }

      const prefix = 0 < this.returnValue ? "+" : "";
      const percentPrefix = 0 < this.returnPercent ? "+" : "";
      return `${prefix}${this.parseNumber(this.returnValue)} (${percentPrefix}${this.parseNumber(this.returnPercent)}%)`;
    },
    smallTextClass() {
      return { "xsmall-font-size": 1200 > this.windowsWidth };
    }
  },
  methods: {
    clampPercent(value) {
      const percent = parseFloat(value);
      return Number.isFinite(percent) ? Math.min(Math.max(percent, 0), 100) : 0;
    },
    t(key, ...args) {
      return this.translator(key, ...args);
    },
    formatPrice(...args) {
      return this.priceFormatter(...args);
    },
    parseNumber(...args) {
      return this.numberParser(...args);
    }
  }
};
</script>

<style scoped>
.instrument-card-title {
  font-size: 1.25rem;
  font-weight: bold;
  line-height: 1.25;
  margin: 0;
  text-align: center;
}

.instrument-card-header {
  align-items: center;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  height: 3.25rem;
  justify-content: center;
  padding: 0 1rem;
}

.equity_align_right_heavy {
  font-weight: bold;
  text-align: right;
}

.positions-summary-table td,
.positions-summary-table th {
  font-size: 1rem !important;
}

.positions-summary-table td {
  line-height: 1.35;
  padding: 0.45rem 0.5rem;
}

.positions-summary-table td:first-child {
  white-space: nowrap;
}

.positions-summary-table {
  margin-bottom: 0;
}

.progress-meter-value {
  align-items: center;
  display: inline-flex;
  gap: 0.25rem;
  white-space: nowrap;
}

.progress-percent {
  display: inline-block;
  min-width: 3.75rem;
  text-align: right;
}

.greenColor {
  color: #189e18;
}

.redColor {
  color: #ff0000;
}

.quote-change-icon {
  display: block;
  flex: 0 0 12px;
  height: 12px;
  margin: 0;
  align-self: center;
  width: 12px;
}

.return-value-with-icon {
  align-items: center;
  display: flex;
  gap: 0.15rem;
  justify-content: flex-end;
  line-height: 1;
  min-height: 1.2rem;
  width: 100%;
}

.return-value-text {
  transform: translateY(1px);
}

.summary-card-body {
  padding: 0.75rem 1rem 0.25rem;
}

.wallet-summary-card .btn {
  display: block;
  margin: 0.5rem 1rem 0.75rem !important;
  padding: 12px 60px;
  width: calc(100% - 2rem);
}
</style>
